/**
 * Что разрешено во вклеенном знаке партнёра.
 *
 * Знак бренда ВКЛЕИВАЕТСЯ в документ кабинета, а не подключается картинкой:
 * во внешнем изображении `currentColor` разрешается против собственного
 * документа SVG, где `color` имеет начальное значение — чёрный. То есть моно-знак
 * на тёмной теме почти исчез бы, а `"mono": true` в манифесте перестал бы
 * что-либо значить.
 *
 * Цена вклейки — чужая разметка внутри нашего origin. Файл приходит от партнёра,
 * едет через наш бэкенд и потому выглядит «своим»; SVG при этом полноценный
 * документ: `<script>`, `on*`-обработчики, `<foreignObject>` с HTML, SMIL-анимация,
 * подменяющая атрибут, внешние ссылки. Guard-тесты (`tests/test_brand_pack_guard.py`)
 * то же самое стерегут в CI, но CI смотрит на файл в репозитории, а вклейка
 * происходит в браузере клиента — правило нужно в обоих местах.
 *
 * Поэтому здесь БЕЛЫЙ список, а не чёрный: перечислено то, из чего рисуется знак,
 * всё прочее выбрасывается молча. Чёрный список пришлось бы дополнять каждый раз,
 * когда в SVG появляется новый способ исполнить код, — и узнавали бы мы об этом
 * последними.
 *
 * Модуль чистый (строки на входе, `boolean` на выходе) — DOM здесь не нужен, и
 * правило проверяется тестами без браузера; вклейку делает `pack.ts`.
 */

/**
 * Элементы, из которых рисуется знак.
 *
 * `<title>` и `<desc>` НЕ разрешены намеренно — это не безопасность, а
 * доступность: см. `pack.ts`, знак остаётся немым, а имя бренда рядом с ним —
 * настоящий текст. `<image>`, `<style>`, `<foreignObject>`, `<animate>`/`<set>`
 * не разрешены как раз по безопасности: первые два тянут внешнее содержимое,
 * третий вносит HTML, последние умеют подменять атрибут после вклейки.
 */
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'svg', 'g', 'path', 'polygon', 'polyline', 'circle', 'ellipse', 'rect', 'line',
  'defs', 'symbol', 'use', 'clippath', 'mask',
  'lineargradient', 'radialgradient', 'stop',
])

/**
 * Атрибуты геометрии и подачи. Регистр здесь не важен (`viewBox` ищется как
 * `viewbox`), но ставится атрибут ПОД ИСХОДНЫМ именем: в SVG `viewBox` и
 * `gradientUnits` регистрозависимы.
 */
const ALLOWED_ATTRS: ReadonlySet<string> = new Set([
  'id', 'viewbox', 'preserveaspectratio', 'transform', 'd', 'points', 'pathlength',
  'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'fx', 'fy', 'fr',
  'width', 'height', 'offset',
  'fill', 'fill-rule', 'fill-opacity', 'clip-rule', 'opacity',
  'stroke', 'stroke-width', 'stroke-opacity', 'stroke-linecap', 'stroke-linejoin',
  'stroke-miterlimit', 'stroke-dasharray', 'stroke-dashoffset', 'vector-effect',
  'stop-color', 'stop-opacity', 'gradientunits', 'gradienttransform', 'spreadmethod',
  'clippathunits', 'maskunits', 'maskcontentunits', 'clip-path', 'mask',
])

/** Атрибуты-ссылки: разрешены только внутрь того же документа (`#id`). */
const REF_ATTRS: ReadonlySet<string> = new Set(['href', 'xlink:href'])

export function isAllowedTag(tag: string): boolean {
  return ALLOWED_TAGS.has(tag.toLowerCase())
}

/** Ссылка внутрь того же документа: `#gradient`, а не чужой адрес и не схема. */
export function isLocalReference(value: string): boolean {
  return value.startsWith('#') && value.length > 1
}

export function isReferenceAttribute(name: string): boolean {
  return REF_ATTRS.has(name.toLowerCase())
}

/**
 * Пускать ли атрибут внутрь документа.
 *
 * Значение проверяется отдельно от имени: `fill="url(#grad)"` — обычная заливка
 * градиентом, а `fill="url(https://…)"` увёл бы отрисовку на чужой хост, то есть
 * стал бы маячком посещения кабинета.
 */
export function isAllowedAttribute(name: string, value: string): boolean {
  const key = name.toLowerCase()
  // Явный отказ обработчикам — защита вглубь: в белом списке их и так нет, но
  // правило «ни одного on*» должно быть написано, а не выводиться из полноты
  // перечня. Одна невнимательная строка в списке иначе открывает исполнение.
  if (key.startsWith('on')) return false
  if (isReferenceAttribute(key)) return isLocalReference(value)
  if (!ALLOWED_ATTRS.has(key)) return false
  return isSafeValue(value)
}

/** Значение атрибута: без схем и без ссылок наружу документа. */
function isSafeValue(value: string): boolean {
  const flat = value.toLowerCase().replace(/\s+/g, '')
  if (flat.includes('javascript:') || flat.includes('data:')) return false
  // `url(` допустим только локальный: url(#id).
  return !/url\((?!#|'#|"#)/.test(flat)
}
